import api from '../../services/api';

/** Packing station API (rebuilt): rows of what arrived from the lines, ledger entries, flags, labels. */

export interface PackingRow {
  pivot_id: number;
  production_item_id: number;
  assignment_id: number;
  tydzien: string | null;
  linia: string | null;
  location: string | null;
  material: string | null;
  srednica: string | null;
  waga: string | null;
  szpula: string | null;
  marka: string | null;
  kolor: string | null;
  nazwa: string | null;
  indeks: string | null;
  ean: string | null;
  big_spool_line: boolean;
  planned: number;
  arrived: number;
  surplus: number;
  accepted_surplus: number;
  packed: number;
  defects: number;
  to_pack: number;
  not_arrived: number;
  buffer_since: string | null;
  overdue: boolean;
  priority_note: string | null;
  no_cartons: boolean;
  labels_missing_qty: number | null;
}

/** Site / line / week chosen at this computer (each site packs only its own lines). */
export interface PackingFilters {
  location: string;
  lineId: string;
  week: string;
}

export interface FilterOptions {
  locations: string[];
  lines: { id: number; name: string; location: string | null }[];
  weeks: string[];
}

export const EMPTY_FILTERS: PackingFilters = { location: '', lineId: '', week: '' };

const filterParams = (f: PackingFilters) => ({
  location: f.location || undefined,
  line_id: f.lineId || undefined,
  week: f.week || undefined,
});

export interface PackingPage {
  data: PackingRow[];
  meta: {
    current_page: number;
    last_page: number;
    total: number;
    counts: { open: number; overdue: number };
    overdue_days: number;
    filters: FilterOptions;
  };
}

export interface PackingChain {
  planned: number;
  arrived: number;
  not_arrived: number;
  surplus: number;
  accepted_surplus: number;
  packed: number;
  defects: number;
  to_pack: number;
  big_spool_line: boolean;
  line_reported_kg?: number;
  spools_sent?: number;
  spools_waiting?: number;
  waiting_kg?: number;
}

export interface PackingEntry {
  id: number;
  kind: 'packed' | 'defect';
  quantity: number;
  note: string | null;
  user: string | null;
  /** `wfmag`: marked as packed by a collective label printed in WF-MAG (note = document number). */
  source: 'packer' | 'wfmag';
  created_at: string | null;
}

export interface PackingDetailsData {
  row: PackingRow;
  chain: PackingChain;
  entries: PackingEntry[];
}

export interface LabelRequest {
  id: number;
  source: 'missing_labels' | 'surplus';
  /** Surplus added by hand by an admin — its row has no line or week. */
  is_manual: boolean;
  quantity: number;
  requested_by: string | null;
  requested_at: string | null;
  printed_at: string | null;
  printed_by: string | null;
  row: PackingRow | null;
}

/** One position in "Przekazane do magazynu": complete = everything produced so far is packed. */
export interface Handover {
  pivot_id: number;
  status: 'complete' | 'partial';
  handed_over: number;
  last_at: string | null;
  users: string[];
  indeks: string | null;
  nazwa: string | null;
  marka: string | null;
  tydzien: string | null;
  linia: string | null;
  location: string | null;
  planned: number;
  packed: number;
  defects: number;
  to_pack: number;
}

export interface HandoverPage {
  data: Handover[];
  meta: {
    current_page: number;
    last_page: number;
    total: number;
    users: { id: number; name: string }[];
    filters: FilterOptions;
  };
}

export interface DefectReason {
  id: number;
  name: string;
  is_active: boolean;
  sort_order: number;
}

export interface PackingInstruction {
  id: number;
  scope: 'brand' | 'index';
  brand: string | null;
  catalog_index: string | null;
  /** The instruction is a PDF; its bytes come from fetchInstructionFile. */
  file_name: string;
  file_size: number;
  updated_by?: string | null;
  updated_at: string | null;
}

export const fetchRows = async (view: 'open' | 'overdue', q: string, filters: PackingFilters, page: number): Promise<PackingPage> =>
  (await api.get('/packing/rows', { params: { view, q: q || undefined, ...filterParams(filters), page } })).data;

export const fetchDetails = async (pivotId: number): Promise<PackingDetailsData> =>
  (await api.get(`/packing/rows/${pivotId}`)).data.data;

export const addEntry = (pivotId: number, kind: 'packed' | 'defect', quantity: number, defectReasonId?: number | null) =>
  api.post(`/packing/rows/${pivotId}/entries`, { kind, quantity, defect_reason_id: kind === 'defect' ? defectReasonId : undefined });

export const fetchInstruction = async (pivotId: number): Promise<PackingInstruction | null> =>
  (await api.get(`/packing/rows/${pivotId}/instruction`)).data.data;

export const setFlags = (pivotId: number, flags: { no_cartons?: boolean; labels_missing_qty?: number | null }) =>
  api.put(`/packing/rows/${pivotId}/flags`, flags);

export const setPriority = (pivotId: number, note: string | null) =>
  api.put(`/packing/rows/${pivotId}/priority`, { note });

export const deleteEntry = (entryId: number) => api.delete(`/packing/entries/${entryId}`);

export const saveOverdueDays = (days: number) => api.put('/packing/settings', { overdue_days: days });

export const fetchSurplus = async (): Promise<LabelRequest[]> => (await api.get('/packing/surplus')).data.data;

export const acceptSurplus = (requestId: number) => api.post(`/packing/label-requests/${requestId}/accept`);

export const fetchLabelsToPrint = async (): Promise<LabelRequest[]> =>
  (await api.get('/packing/label-requests')).data.data;

export const markLabelsPrinted = (requestId: number) => api.post(`/packing/label-requests/${requestId}/printed`);

export const fetchHandovers = async (
  date: string,
  userId: string,
  status: string,
  filters: PackingFilters,
  page: number,
): Promise<HandoverPage> =>
  (await api.get('/packing/history', {
    params: { date: date || undefined, user_id: userId || undefined, status: status || undefined, ...filterParams(filters), page },
  })).data;

export const fetchDefectReasons = async (): Promise<DefectReason[]> => (await api.get('/packing/defect-reasons')).data.data;

export const saveDefectReason = (reason: Partial<DefectReason> & { name: string }, id?: number) =>
  id ? api.put(`/packing/defect-reasons/${id}`, reason) : api.post('/packing/defect-reasons', reason);

export const deleteDefectReason = (id: number) => api.delete(`/packing/defect-reasons/${id}`);

export const reorderDefectReasons = (ids: number[]) => api.put('/packing/defect-reasons/order', { ids });

export const fetchInstructions = async (): Promise<{ data: PackingInstruction[]; meta: { brands: string[] } }> =>
  (await api.get('/packing/instructions')).data;

/** Multipart: a new PDF is required when adding; when editing, no file keeps the current one. */
export const saveInstruction = (body: { brand: string | null; catalog_index: string | null; file: File | null }, id?: number) => {
  const form = new FormData();
  if (body.brand !== null) form.append('brand', body.brand);
  if (body.catalog_index !== null) form.append('catalog_index', body.catalog_index);
  if (body.file) form.append('file', body.file);
  if (id) form.append('_method', 'PUT');
  // The shared client defaults to JSON, which would turn the form into JSON and drop the file.
  return api.post(id ? `/packing/instructions/${id}` : '/packing/instructions', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const fetchInstructionFile = async (id: number): Promise<ArrayBuffer> =>
  (await api.get(`/packing/instructions/${id}/file`, { responseType: 'arraybuffer' })).data;

export const deleteInstruction = (id: number) => api.delete(`/packing/instructions/${id}`);

/** First validation message of a failed request, or the fallback. */
export const errorMessage = (error: any, fallback: string): string => {
  const errors = error?.response?.data?.errors;
  const first = errors ? (Object.values(errors)[0] as string[] | undefined)?.[0] : undefined;
  return first || error?.response?.data?.message || fallback;
};

export const formatDateTime = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' }) : '—';

export const formatDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('pl-PL') : '—';
